import React from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useBountifulSeaGame } from '../../hooks/useBountifulSeaGame';
import { useBountifulSeaAudio } from '../../hooks/useBountifulSeaAudio';
import { GameSetup } from './GameSetup';
import { PlayerTurnTransition } from './PlayerTurnTransition';
import { VoyagePhase } from './VoyagePhase';
import { RoundSummary } from './RoundSummary';
import { VictoryScreen } from './VictoryScreen';
import '../../styles/BountifulSea.css';

// ═══════════════════════════════════════════════════════════════
// BOUNTIFUL SEA - MAIN GAME COMPONENT
// ═══════════════════════════════════════════════════════════════

const pageVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.5 } },
  exit: { opacity: 0, transition: { duration: 0.3 } },
};

export const BountifulSeaGame: React.FC = () => {
  const [state, actions, helpers] = useBountifulSeaGame();

  // Audio management - plays game music during play, victory music at end
  const { masterVolume, setMasterVolume, isMuted, toggleMute } = useBountifulSeaAudio(state.phase);

  const currentPlayer = state.players[state.round.currentPlayerId];

  return (
    <div className="bountiful-sea">
      {/* Header */}
      <header className="bs-header">
        <Link to="/creative" className="bs-back-link">
          <span className="bs-back-arrow">&#8592;</span>
          <span>Back to Creative</span>
        </Link>
        <h1 className="bs-title">Bountiful Sea</h1>

        {/* Volume Controls */}
        <div className="bs-volume-controls">
          <button
            className="bs-volume-btn"
            onClick={toggleMute}
            title={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? '🔇' : masterVolume > 0.5 ? '🔊' : masterVolume > 0 ? '🔉' : '🔈'}
          </button>
          <input
            type="range"
            className="bs-volume-slider"
            min={0}
            max={100}
            value={isMuted ? 0 : masterVolume * 100}
            onChange={(e) => {
              const newVolume = Number(e.target.value) / 100;
              setMasterVolume(newVolume);
              if (newVolume > 0 && isMuted) {
                toggleMute();
              }
            }}
            title={`Volume: ${Math.round(masterVolume * 100)}%`}
          />
        </div>
      </header>

      {/* Main Content */}
      <main className="bs-main">
        <AnimatePresence mode="wait">
          {/* Setup Phase */}
          {state.phase === 'setup' && (
            <motion.div
              key="setup"
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <GameSetup onStartGame={actions.startGame} />
            </motion.div>
          )}

          {/* Player Transition (Hot-Seat Privacy) */}
          {state.phase === 'playerTransition' && currentPlayer && (
            <motion.div
              key="transition"
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <PlayerTurnTransition
                playerName={currentPlayer.name}
                playerColor={currentPlayer.color}
                roundNumber={state.round.roundNumber}
                isAI={currentPlayer.isAI}
                onConfirm={actions.confirmPlayerReady}
              />
            </motion.div>
          )}

          {/* Voyage Phase (Main Gameplay) */}
          {state.phase === 'voyage' && (
            <motion.div
              key="voyage"
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              style={{ width: '100%' }}
            >
              <VoyagePhase
                state={state}
                actions={actions}
                helpers={helpers}
              />
            </motion.div>
          )}

          {/* Round End Summary */}
          {state.phase === 'roundEnd' && (
            <motion.div
              key="roundEnd"
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <RoundSummary
                state={state}
                onContinue={actions.confirmRoundSummary}
              />
            </motion.div>
          )}

          {/* Victory Screen */}
          {state.phase === 'victory' && (
            <motion.div
              key="victory"
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <VictoryScreen
                state={state}
                onPlayAgain={actions.resetGame}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Game Message */}
        {state.message && state.phase !== 'setup' && (
          <motion.div
            className="bs-message"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            key={state.message}
          >
            {state.message}
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default BountifulSeaGame;
